// C:\Users\Anish\xpr_survey\XPRSuvey-App\src\Components\Survey\Pages\Company\Dashboard.jsx

import React, { useState, useMemo, useEffect } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Building2,
    Plus,
    Filter,
    Search,
    ChevronDown,
    Eye,
    Pencil,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import { CompanyDropdown } from "../../Services/MasterDataService";

const STATUS_OPTIONS = ["All Status", "Active", "Inactive"];
const ITEMS_PER_PAGE = 10; // Change this to set how many rows to display per page

const SortableHeader = ({ children }) => (
    <th className="py-3 pr-3">
        <span className="flex items-center gap-1">
            {children}
        </span>
    </th>
);

const TrendBadge = ({ trend, value }) => (
    <span
        className={`flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${trend === "up" ? "bg-[#c2a8f026] text-[#7f35b2]" : "bg-red-50 text-red-500"
            }`}
    >
        {trend === "up" ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
        {value}
    </span>
);

const CompanyDashboard = () => {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("All Status");

    // State to hold API data
    const [companies, setCompanies] = useState([]);

    // Fetch data on component mount
    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const response = await CompanyDropdown({});

                if (response && response.data && response.data.status === "success") {
                    const fetchedData = response.data.companyList.map((company) => ({
                        id: company.companyID,
                        companyUID: company.companyUID,
                        name: company.companyName,
                        contactNumber: company.contactNumber || "-",
                        contactMail: company.contactMail || "-",
                        status: company.status ? "Active" : "Inactive",
                        dateCreated: company.establishmentDate,
                    }));

                    setCompanies(fetchedData);
                }
            } catch (error) {
               
            }
        };

        fetchCompanies();
    }, []);

    // Reset pagination to first page when search or status filter changes
    useEffect(() => {
        setPage(1);
    }, [search, statusFilter]);

    // Calculate dynamic counts for the stats cards
    const totalCompaniesCount = companies.length;
    const activeCompaniesCount = companies.filter(c => c.status === "Active").length;
    const inactiveCompaniesCount = companies.filter(c => c.status === "Inactive").length;

    // Dynamic stat cards configuration
    const STAT_CARDS = [
        { label: "Total Companies", value: totalCompaniesCount, sub: "All registered companies", icon: Building2, iconBg: "bg-[#c2a8f033]", iconColor: "text-[#7f35b2]" },
        { label: "Active Companies", value: activeCompaniesCount, sub: "Currently active", icon: Building2, iconBg: "bg-blue-50", iconColor: "text-blue-500" },
        { label: "Inactive Companies", value: inactiveCompaniesCount, sub: "Currently inactive", icon: Building2, iconBg: "bg-orange-50", iconColor: "text-orange-500" },
    ];

    // 1. Filter the entire list first
    const filteredCompanies = useMemo(() => {
        const term = search.trim().toLowerCase();
        return companies.filter((company) => {
            const matchesSearch =
                term === "" ||
                (company.name && company.name.toLowerCase().includes(term)) ||
                (company.contactMail && company.contactMail.toLowerCase().includes(term)) ||
                (company.contactNumber && company.contactNumber.toLowerCase().includes(term));
            const matchesStatus = statusFilter === "All Status" || company.status === statusFilter;
            return matchesSearch && matchesStatus;
        });
    }, [search, statusFilter, companies]);

    // 2. Client-side Pagination Logic
    const totalPages = Math.ceil(filteredCompanies.length / ITEMS_PER_PAGE);

    // Slice the filtered data for the current page
    const paginatedCompanies = useMemo(() => {
        const startIndex = (page - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredCompanies.slice(startIndex, endIndex);
    }, [filteredCompanies, page]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="mx-auto max-w-full p-4"
        >
            {/* Page Header */}
            <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Company Management</h1>
                    <p className="mt-1 text-sm text-gray-500">View, manage and maintain all company information in one place.</p>
                </div>
                <Link
                    to="/dashboard/companies/add-company"
                    className="flex items-center gap-2 rounded-lg bg-[#7f35b2] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#3bb290]"
                >
                    <Plus size={16} />
                    Add New Company
                </Link>
            </div>

            {/* Stat Cards */}
            <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {STAT_CARDS.map((card) => {
                    const Icon = card.icon;
                    return (
                        <div key={card.label} className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                            <div className="flex items-center gap-3">
                                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${card.iconBg}`}>
                                    <Icon size={20} className={card.iconColor} />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">{card.label}</p>
                                    <p className="text-xl font-bold text-gray-900">{card.value}</p>
                                </div>
                            </div>
                            <div className="mt-3 flex items-center justify-between">
                                <p className="text-xs text-gray-400">{card.sub}</p>
                                
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Company List Card */}
            <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                <div className="flex justify-between">
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                        <h2 className="text-base font-semibold text-gray-900">Company List</h2>
                    </div>

                    {/* Search + Status Filter */}
                    <div className="mb-4 flex flex-wrap justify-end items-center gap-3">
                        <div className="relative min-w-[220px] flex-1">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by company name, mobile or email..."
                                className="w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]"
                            />
                        </div>

                        <div className="relative">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="appearance-none rounded-lg border border-gray-200 py-2.5 pl-3 pr-8 text-sm text-gray-700 outline-none focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]"
                            >
                                {STATUS_OPTIONS.map((status) => (
                                    <option key={status} value={status}>
                                        {status}
                                    </option>
                                ))}
                            </select>
                            <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        </div>
                        
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                <SortableHeader>Company Name</SortableHeader>
                                <SortableHeader>Contact Number</SortableHeader>
                                <SortableHeader>Contact Mail</SortableHeader>
                                <SortableHeader>Status</SortableHeader>
                                {/* <th className="py-3 pr-3">Actions</th> */}
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedCompanies.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="py-8 text-center text-sm text-gray-400">
                                        No companies match your search.
                                    </td>
                                </tr>
                            ) : (
                                paginatedCompanies.map((company) => (
                                    <tr key={company.id} className="border-b border-gray-50 last:border-0">
                                        <td className="py-3 pr-3">
                                            <div className="flex items-center gap-2.5">
                                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#c2a8f033]">
                                                    <Building2 size={15} className="text-[#7f35b2]" />
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        navigate(`/dashboard/companies/${company.companyUID}/assign-assessment`, {
                                                            state: { company },
                                                        })
                                                    }
                                                    className="font-medium text-gray-900 hover:text-[#7f35b2] hover:underline text-left"
                                                >
                                                    {company.name}
                                                </button>
                                            </div>
                                        </td>
                                        <td className="py-3 pr-3 text-gray-500">{company.contactNumber}</td>
                                        <td className="py-3 pr-3 text-gray-500">{company.contactMail}</td>
                                        <td className="py-3 pr-3">
                                            <span
                                                className={`rounded-full px-2.5 py-1 text-xs font-medium ${company.status === "Active" ? "bg-[#c2a8f026] text-[#7f35b2]" : "bg-red-50 text-red-500"
                                                    }`}
                                            >
                                                {company.status}
                                            </span>
                                        </td>
                                        {/* <td className="py-3 pr-3">
                                            <div className="flex items-center gap-2">
                                                <button className="rounded-md p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-600" title="View">
                                                    <Eye size={15} />
                                                </button>
                                                <button className="rounded-md p-1.5 text-[#7f35b2] hover:bg-[#c2a8f033]" title="Edit">
                                                    <Pencil size={15} />
                                                </button>
                                            </div>
                                        </td> */}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
                    <p className="text-gray-500">
                        Showing {filteredCompanies.length === 0 ? 0 : (page - 1) * ITEMS_PER_PAGE + 1} to {Math.min(page * ITEMS_PER_PAGE, filteredCompanies.length)} of {filteredCompanies.length} entries
                    </p>

                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                            disabled={page === 1}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft size={15} />
                        </button>

                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                            <button
                                key={p}
                                onClick={() => setPage(p)}
                                className={`flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium transition-colors ${p === page
                                    ? "bg-[#7f35b2] text-white border border-[#7f35b2]"
                                    : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                                    }`}
                            >
                                {p}
                            </button>
                        ))}

                        <button
                            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                            disabled={page === totalPages || totalPages === 0}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronRight size={15} />
                        </button>
                    </div>
                </div>
            </div>

            <Outlet />
        </motion.div>
    );
};

export default CompanyDashboard;

